import lodashSet from 'lodash/set';
import { useContext } from 'react';
import { create, useStore } from 'zustand';

import {
  QuestionLibraryState,
  QuestionLibraryZustandStore,
} from '../types/store';
import { EventEmitter } from '../utils/eventEmitter';
import { setCitation } from '../utils/utils';
import { QuestionLibraryStoreContext } from './QuestionLibraryStoreContext';

/**
 * Creates a new instance of the Question Library Store
 */
export function createQuestionLibraryStore(
  screenData: QuestionLibraryState['screenData'] = {},
) {
  const questionLibraryStore: QuestionLibraryZustandStore = create(
    (set, get) => ({
      screenData,
      focusedQuestion: null,
      eventEmitter: new EventEmitter(),
      actions: {
        setScreenData: (data) => {
          return set({ screenData: data });
        },
        updateScreenData: (data) => {
          return set((state) => ({
            screenData: { ...state.screenData, ...data },
          }));
        },
        updateScreenDataKey: (key, value) => {
          return set((state) => {
            const clonedScreenData = structuredClone(state.screenData);

            lodashSet(clonedScreenData, key, value);

            return { screenData: clonedScreenData };
          });
        },
        setFocusedQuestion: (questionName, smartAnswer) => {
          return set(() => {
            const snippets =
              smartAnswer?.aiResponse?.snippets ??
              get().functions.getSmartAnswer(questionName)?.aiResponse
                ?.snippets;

            if (snippets) {
              setCitation(snippets);
            }

            return { focusedQuestion: questionName };
          });
        },
      },
      functions: {
        getSmartAnswer: (questionName) => {
          return get().screenData.questionAnswers?.[questionName];
        },
      },
    }),
  );

  return questionLibraryStore;
}

// @ts-expect-error this is fine for now
export const useQuestionLibraryStore: QuestionLibraryZustandStore = (
  selector,
) => {
  const store = useContext(QuestionLibraryStoreContext);

  if (!store) {
    throw new Error(
      'useQuestionLibraryStore was called outside of a QuestionLibraryStoreProvider',
    );
  }

  return useStore(store, selector);
};

// These are helper functions to reduce having to write out a verbose selector.
// These values are stable and will never change so selecting all of actions, functions, etc. is fine.

export function useQuestionLibraryActions() {
  return useQuestionLibraryStore((s) => s.actions);
}

export function useQuestionLibraryFunctions() {
  return useQuestionLibraryStore((s) => s.functions);
}

export function useQuestionLibraryEventEmitter() {
  return useQuestionLibraryStore((s) => s.eventEmitter);
}
